import type {
  ResponseResult,
  SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import { scoredDefs, type SubQuestionTypeRef } from './deriveParentResult';

/**
 * Derive the bulk-answer cluster's "selected" state from the current
 * sub-question responses:
 *
 *   - Every SCORED (Pass/Fail) sub-question has an answered entry
 *     AND every one shares the same result → return that result.
 *   - Anything else (missing entries, undefined result, mixed
 *     results, or zero scored subs) → return undefined ("no bulk
 *     selection").
 *
 * Data-collection (ERT) children are excluded: the bulk cluster only
 * stamps scored children, so their captured values must neither
 * enable nor block the selected state.
 *
 * Pure — extracted from `PerformAuditQuestionInteraction` to keep
 * the interaction under the file-size cap and to unit-test the
 * derived-uniform predicate on its own. This intentionally differs
 * from parent audit aggregation and write-side completion status:
 * bulk selection remains defined only for uniform scored answers.
 */
export function deriveBulkSelectedResult(
  entries: readonly SubQuestionResponseEntry[] | undefined,
  subQuestionRefs: readonly SubQuestionTypeRef[],
): ResponseResult | undefined {
  const scored = scoredDefs(subQuestionRefs);
  if (scored.length === 0) return undefined;

  const entriesById = new Map<string, SubQuestionResponseEntry>();
  for (const entry of entries ?? []) {
    if (entry.subQuestionId) entriesById.set(entry.subQuestionId, entry);
  }

  let first: ResponseResult | undefined;
  for (const def of scored) {
    const entry = def.subQuestionId ? entriesById.get(def.subQuestionId) : undefined;
    const result = entry?.response?.responseResult;
    if (result === undefined) return undefined;
    if (first === undefined) first = result;
    else if (result !== first) return undefined;
  }
  return first;
}
